/**
 * skyship_registry.ts — where each city's sky-ship actually hangs.
 *
 * CommonsSettlementsModule parks the hull (the same `structure:civa_ship`
 * model that used to squat in the fabric) and publishes its MEASURED frame
 * here; SkyShipModule anchors the walkable interior to it — decks ride the
 * hull's spine, the foredeck opens onto its back. A city whose ship has not
 * assembled yet falls back to the analytic pad + SHIP_ALT_M.
 */
/**
 * THE SKY-SHIP MODEL. The user identified it (2026-09-01): `civa_city_hall`
 * — a 72.8×21.2×90 m ship-shaped hall that the civ-A LANDMARK pool blew up
 * to a 1.4 km slab lying across Ostorch. It is no longer fabric; it is the
 * ship over every city, and the only ship (the civa_ship liner that was
 * added in its place is retired).
 */
export const SKY_SHIP_PREFAB = 'structure:civa_city_hall';

export interface SkyShipFrame {
  /** World XZ of the ship origin (the spaceport spot) + facing yaw. */
  x: number; z: number; yaw: number;
  /** World Y of the visible hull's bottom and top (stray-proof bounds). */
  hullBottomY: number; hullTopY: number;
  /** World Y the interior's ship-local origin should sit at. */
  deckBaseY: number;
  /** The hull's long axis is mesh X (else Z) — the interior's bow follows it. */
  longAxisX: boolean;
}

const frames = new Map<number, SkyShipFrame>();

/**
 * THE FRAME IS THE HULL'S CENTER (2026-09-03). The settlement streamer parks
 * the hull by placing its GROUP so that the model's measured bbox center
 * lands on the pad (pivot compensation — the cooked civ GLBs are off-pivot,
 * bbox centers up to 1.3 km from the model origin). The frame it publishes
 * for the boardable interior must therefore be that CENTER, never the group
 * origin: at site 10 the origin sat 3,848 m from the pad, so the interior was
 * built in open sky 3.8 km from the visible ship and the lift target lay far
 * outside the reducer's reach ("The lift does not reach there"). Ostorch hid
 * it — its city never assembled headless, so every probe there boarded via
 * the analytic fallback (the pad). Pure so the harness pins it.
 *   px/pz — the group origin; bbCenterX/Z — the model-space bbox center; the
 *   group is yawed about Y and uniformly scaled.
 */
export function hullCenter(px: number, pz: number, yaw: number, bbCenterX: number, bbCenterZ: number, scale: number): { x: number; z: number } {
  const cx = bbCenterX * scale, cz = bbCenterZ * scale;
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  return { x: px + cx * cos + cz * sin, z: pz - cx * sin + cz * cos };
}

export function registerSkyShip(siteId: number, f: SkyShipFrame): void { frames.set(siteId, f); }
export function unregisterSkyShip(siteId: number): void { frames.delete(siteId); }
export function skyShipFrame(siteId: number): SkyShipFrame | null { return frames.get(siteId) ?? null; }
